import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Header } from '../components/Header';
import { ProductGrid } from '../components/ProductGrid';
import { SignaturePad } from '../components/SignaturePad';
import { useAuth } from '../contexts/AuthContext';
import { Truck, Check, AlertCircle, MapPin, ArrowDown } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function NuevaTransferencia() {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [puntosVenta, setPuntosVenta] = useState([]);
  const [puntoOrigen, setPuntoOrigen] = useState('');
  const [puntoDestino, setPuntoDestino] = useState('');
  const [quantities, setQuantities] = useState({});
  const [showSignature, setShowSignature] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    loadPuntosVenta();
  }, []);

  const loadPuntosVenta = async () => {
    try {
      const { data } = await axios.get(`${API}/puntos-venta`);
      setPuntosVenta(data.sort((a, b) => a.name.localeCompare(b.name, 'es')));
    } catch (error) {
      setError('Error al cargar puntos de venta');
    } finally {
      setLoading(false);
    }
  };

  const handleProceed = () => {
    if (!puntoOrigen || !puntoDestino) {
      setError('Selecciona origen y destino');
      return;
    }
    if (puntoOrigen === puntoDestino) {
      setError('Origen y destino deben ser diferentes');
      return;
    }
    if (Object.values(quantities).every(v => !v || v === 0)) {
      setError('Selecciona al menos un producto');
      return;
    }
    setError('');
    setShowSignature(true);
  };

  const handleSignatureSave = async (signatureData) => {
    setShowSignature(false);
    setSubmitting(true);
    
    try {
      await axios.post(`${API}/transferencias`, {
        punto_origen_id: puntoOrigen,
        punto_destino_id: puntoDestino,
        productos: quantities,
        firma_origen: signatureData,
      });
      navigate('/transferencias');
    } catch (error) {
      setError(error.response?.data?.detail || 'Error al crear transferencia');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="nueva-transferencia-page">
      <Header title="Nueva Entrega" showBack onBack={() => navigate('/transferencias')} />
      
      <div className="page-container fade-in">
        {error && (
          <div className="mb-6 p-4 bg-red-50 border-2 border-red-200 rounded-xl flex items-center gap-3">
            <AlertCircle className="w-6 h-6 text-red-600" />
            <p className="text-red-700 font-medium">{error}</p>
          </div>
        )}

        {/* Origen */}
        <div className="mb-6">
          <h3 className="text-lg font-bold text-zinc-900 mb-3 flex items-center gap-2">
            <MapPin className="w-5 h-5 text-blue-600" />
            Punto de Origen
          </h3>
          <div className="grid grid-cols-2 gap-3">
            {puntosVenta.map((punto) => (
              <button
                key={punto.id}
                onClick={() => setPuntoOrigen(punto.id)}
                className={`h-16 rounded-xl font-bold btn-press ${
                  puntoOrigen === punto.id 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-white border-2 border-zinc-200'
                }`}
                data-testid={`origen-${punto.id}`}
              >
                {punto.name}
              </button>
            ))}
          </div>
        </div>

        {/* Arrow */}
        <div className="flex justify-center mb-6">
          <ArrowDown className="w-8 h-8 text-zinc-400" />
        </div>

        {/* Destino */}
        <div className="mb-6">
          <h3 className="text-lg font-bold text-zinc-900 mb-3 flex items-center gap-2">
            <MapPin className="w-5 h-5 text-green-600" />
            Punto de Destino
          </h3>
          <div className="grid grid-cols-2 gap-3">
            {puntosVenta.map((punto) => (
              <button
                key={punto.id}
                onClick={() => setPuntoDestino(punto.id)}
                disabled={punto.id === puntoOrigen}
                className={`h-16 rounded-xl font-bold btn-press disabled:opacity-50 ${
                  puntoDestino === punto.id 
                    ? 'bg-green-600 text-white' 
                    : 'bg-white border-2 border-zinc-200'
                }`}
                data-testid={`destino-${punto.id}`}
              >
                {punto.name}
              </button>
            ))}
          </div>
        </div>

        {/* Products */}
        <div className="mb-6">
          <h3 className="text-lg font-bold text-zinc-900 mb-3 flex items-center gap-2">
            <Truck className="w-5 h-5" />
            Productos a Entregar
          </h3>
          
          <ProductGrid
            quantities={quantities}
            onQuantityChange={(clave, qty) => setQuantities({ ...quantities, [clave]: qty })}
          />
        </div>

        {/* Submit */}
        <button
          onClick={handleProceed}
          disabled={submitting}
          className="w-full h-20 bg-blue-600 text-white rounded-2xl text-2xl font-bold btn-press flex items-center justify-center gap-3 disabled:opacity-50"
          data-testid="btn-proceed-transfer"
        >
          {submitting ? (
            <div className="spinner w-8 h-8 border-white border-t-transparent" />
          ) : (
            <>
              <Check className="w-8 h-8" />
              Firmar y Crear Entrega
            </>
          )}
        </button>
      </div>

      {showSignature && (
        <SignaturePad
          title="Firma de Salida"
          onSave={handleSignatureSave}
          onCancel={() => setShowSignature(false)}
        />
      )}
    </div>
  );
}
